/**
 * OCTOPUS - MotorsConfig (Phase 999.4 Plan 03, split do LiveTelemetryConfig — D-03)
 *
 * Aba "Motores" do diálogo Editar Equipamento: CADASTRO FÍSICO dos motores —
 * papel (Propulsão / Draga / Motor), posição no casco (Bombordo / Centro /
 * Boreste), modelo do catálogo (curva de carga herdada) e regimes de operação
 * (faixas RPM × L/h × engate). NÃO lida com visibilidade de sinais — isso é
 * responsabilidade do `SignalsConfig` (aba "Telemetria (Ao vivo)").
 *
 * Payload PARCIAL (não-destrutivo): o handler `PUT /equipments/{id}/live-config`
 * (backend/routes/equipment.py) trata `motors`/`remove_motors` e `signals` em
 * blocos `if` INDEPENDENTES — `LiveConfigUpdate` tem defaults `[]` para os 3
 * campos, então omitir `signals` do payload NUNCA apaga sinais (confirmado
 * lendo o handler antes do split, Pitfall A4 do plano). Este componente só
 * envia `{ motors, remove_motors }`.
 *
 * Autônomo: busca seu próprio GET /equipments/{id}/live-view ao montar (o
 * Radix Tabs desmonta o conteúdo inativo — o fetch só acontece quando a aba
 * "Motores" está aberta).
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Plus, Trash2, Undo2, Gauge, ArrowLeftRight, ChevronDown, ChevronUp, Siren } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { severityConfig } from '@/lib/alertSeverity';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { showErrorToast } from '@/utils/errorHandler';
import EngineModelsManager from './EngineModelsManager';

const NO_MODEL = '__none__'; // sentinela do Select (Radix não aceita value="")

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: 'propulsion', label: 'Propulsão' },
  { value: 'draga', label: 'Draga / Bomba' },
  { value: 'motor', label: 'Motor (genérico)' },
];

const POSITION_OPTIONS = [
  { value: 'port', label: 'Bombordo (BB)' },
  { value: 'center', label: 'Centro' },
  { value: 'starboard', label: 'Boreste (BE)' },
];

const GEAR_OPTIONS = [
  { value: 'any', label: 'Qualquer' },
  { value: 'neutral', label: 'Neutro' },
  { value: 'engaged', label: 'Engatada' },
];

// Severidade curada por warning (D-09). MESMOS values/labels da paleta do
// UI-SPEC / severityConfig da Central (@/lib/alertSeverity) — zero drift (D-01).
const SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Crítica' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Média' },
  { value: 'low', label: 'Baixa' },
];

// Regras de alerta por motor (Fase 17.1, D-21/D-22/D-23): tipo/operador/estado
// digital — vocabulário IDÊNTICO ao allowlist de `sanitize_motor_rules`
// (backend/live_view.py) para nunca gerar uma regra que o backend descarta
// silenciosamente. Path do dropdown NUNCA é uma lista curada — vem 100%
// data-driven do live-view (analogPathOptions/digitalPathOptions abaixo),
// decisão do dono no checkpoint deste plano (zero hardcode).
const RULE_TYPE_OPTIONS = [
  { value: 'analog', label: 'Analógica (threshold)' },
  { value: 'digital', label: 'Digital (0/1)' },
];

const RULE_OP_OPTIONS = [
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
];

const ACTIVE_WHEN_OPTIONS = [
  { value: '1', label: 'ATIVO (1)' },
  { value: '0', label: 'INATIVO (0)' },
];

// SUGESTÕES de regimes por papel (diretiva do dono 2026-07-03) — só preenchem
// o editor; os valores salvos são set-points POR MOTOR (calibráveis, nada
// hard-coded no runtime). Ordem significativa: primeira regra que casa vence.
const SUGGESTED_REGIMES = {
  draga: [
    { id: 'bomba_succao', label: 'Bomba de sucção', rpm_min: null, rpm_max: null, fuel_lph_min: 25, fuel_lph_max: null, gear: null },
    { id: 'apoio_hidraulico', label: 'Apoio hidráulico', rpm_min: 1100, rpm_max: null, fuel_lph_min: null, fuel_lph_max: 25, gear: null },
    { id: 'marcha_lenta', label: 'Marcha lenta', rpm_min: 1, rpm_max: 1100, fuel_lph_min: null, fuel_lph_max: 6, gear: null },
  ],
  propulsion: [
    { id: 'propulsao', label: 'Propulsão', rpm_min: null, rpm_max: null, fuel_lph_min: null, fuel_lph_max: null, gear: 'engaged' },
    { id: 'gerando_energia', label: 'Gerando energia', rpm_min: 1, rpm_max: null, fuel_lph_min: null, fuel_lph_max: 15, gear: 'neutral' },
    { id: 'marcha_lenta', label: 'Marcha lenta', rpm_min: 1, rpm_max: 900, fuel_lph_min: null, fuel_lph_max: 6, gear: null },
  ],
  motor: [
    { id: 'trabalho', label: 'Em trabalho', rpm_min: 1100, rpm_max: null, fuel_lph_min: null, fuel_lph_max: null, gear: null },
    { id: 'marcha_lenta', label: 'Marcha lenta', rpm_min: 1, rpm_max: 1100, fuel_lph_min: null, fuel_lph_max: null, gear: null },
  ],
};

const MotorsConfig = ({ equipmentId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState(null);
  // TODOS os grupos do live-view (Fase 17.1-07), não só kind==='motor': os
  // paths digitais (notifications.propulsion.{motorId}.*) caem no grupo
  // "Outros" em build_live_view (motor_instance_from_path só reconhece o
  // prefixo `propulsion.{id}.`, não `notifications.propulsion.{id}.`) — o
  // dropdown digital precisa varrer todos os grupos para achar os paths do
  // motor. 100% data-driven, zero lista curada (decisão do dono no checkpoint).
  const [allGroups, setAllGroups] = useState([]);
  const [motorEdits, setMotorEdits] = useState({});   // {motorId: {role, position, regimes, engine_model_id}}
  const [removeMotors, setRemoveMotors] = useState([]); // [motorId] a descadastrar
  const [engineModels, setEngineModels] = useState([]); // catálogo de modelos de motor
  // Auditoria dos eventos-sombra de reversão (D-05, Fase 17): estado local por
  // motor — expandido/carregando/eventos já buscados nesta sessão (sem cache
  // entre motores; fetch só ao expandir, igual ao lazy-load do restante do arquivo).
  const [expandedGearboxEvents, setExpandedGearboxEvents] = useState({}); // {motorId: bool}
  const [gearboxEvents, setGearboxEvents] = useState({}); // {motorId: events[]}
  const [loadingGearboxEvents, setLoadingGearboxEvents] = useState({}); // {motorId: bool}

  const fetchEngineModels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/engine-models`);
      setEngineModels(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar catálogo de motores:', error);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    if (!equipmentId) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/equipments/${equipmentId}/live-view`,
        { params: { include_hidden: true } }
      );
      const liveGroups = res.data?.groups || [];
      setGroups(liveGroups.filter((g) => g.kind === 'motor'));
      setAllGroups(liveGroups);
      setMotorEdits({});
      setRemoveMotors([]);
    } catch (error) {
      console.error('Erro ao carregar cadastro de motores:', error);
      showErrorToast(toast, error, 'Erro ao carregar motores');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => {
    if (equipmentId) {
      fetchConfig();
      fetchEngineModels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentId]);

  const effectiveRole = (group) => motorEdits[group.motor_id]?.role ?? group.role;
  const effectivePosition = (group) => motorEdits[group.motor_id]?.position ?? group.position;
  const effectiveRegimes = (group) => motorEdits[group.motor_id]?.regimes ?? group.regimes ?? [];
  const effectiveModelId = (group) => motorEdits[group.motor_id]?.engine_model_id ?? group.engine_model_id ?? null;
  // Catálogo EFETIVO (seed global + override por-motor) do Plano 03: override
  // local desta sessão vence; senão o group.warnings_catalog do live-view.
  const effectiveWarnings = (group) => motorEdits[group.motor_id]?.warnings_catalog ?? group.warnings_catalog ?? {};
  // Params de gearbox EFETIVOS por-motor (Fase 17, mesmo molde de effectiveWarnings):
  // override local desta sessão vence; senão o group.gearbox cru do live-view
  // (sem herança de seed — cada motor calibra o próprio ratio, D-06/17-02).
  const effectiveGearbox = (group) => motorEdits[group.motor_id]?.gearbox ?? group.gearbox ?? {};
  // Regras de alerta EFETIVAS por-motor (Fase 17.1, D-21/D-22/D-23): override
  // local desta sessão vence; senão o group.alert_rules cru do live-view (sem
  // herança de seed, mesmo padrão de gearbox — {} = motor sem regra, sombra).
  const effectiveRules = (group) => motorEdits[group.motor_id]?.alert_rules ?? group.alert_rules ?? {};

  // Paths ANALÓGICOS candidatos do motor: 100% data-driven a partir do que o
  // motor JÁ publica no live-view (`group.sources`, mesma fonte do
  // /sources-live) — decisão do dono no checkpoint deste plano (zero lista
  // curada). Só numéricos (display_value number); path devolvido é RELATIVO
  // ao motor (prefixo `propulsion.{motor_id}.` removido) porque o worker
  // (engine_rule_worker._rule_field) reprefixa na leitura.
  const analogPathOptions = (group) => {
    const prefix = `propulsion.${group.motor_id}.`;
    return (group.sources || [])
      .filter((s) => s.path && s.path.startsWith(prefix) && typeof s.display_value === 'number')
      .map((s) => ({ path: s.path.slice(prefix.length), label: s.display_name || s.path.slice(prefix.length) }));
  };

  // Paths DIGITAIS candidatos do motor: também 100% data-driven, mas
  // `notifications.propulsion.{motor_id}.*` NÃO cai no grupo do motor em
  // build_live_view (motor_instance_from_path só casa o prefixo
  // `propulsion.{id}.`, não `notifications.propulsion.{id}.`) — esses paths
  // ficam no grupo "Outros". Por isso varremos `allGroups` inteiro (todos os
  // kinds) e filtramos pelo prefixo do motor. Path devolvido é o COMPLETO já
  // qualificado (o backend/worker usa ele cru, sem reprefixar — D-22).
  const digitalPathOptions = (motorId) => {
    const prefix = `notifications.propulsion.${motorId}.`;
    const seen = new Set();
    const opts = [];
    (allGroups || []).forEach((g) => {
      (g.sources || []).forEach((s) => {
        if (s.path && s.path.startsWith(prefix) && !seen.has(s.path)) {
          seen.add(s.path);
          opts.push({ path: s.path, label: s.display_name || s.path.slice(prefix.length) });
        }
      });
    });
    return opts;
  };

  // Defensivo: se o path salvo não aparecer mais entre as opções vivas (ex.:
  // path parou de publicar), inclui-o mesmo assim no topo da lista — o Save
  // nunca deve "sumir" com uma regra calibrada por falta de dado momentâneo.
  const withCurrentPath = (options, currentPath) => {
    if (currentPath && !options.some((opt) => opt.path === currentPath)) {
      return [{ path: currentPath, label: currentPath }, ...options];
    }
    return options;
  };

  // Associa o motor a um modelo do catálogo (herda a curva) ou desassocia (NO_MODEL).
  // A curva vive SÓ no catálogo (dono 2026-07-08): o cliente envia APENAS o
  // engine_model_id — o backend resolve/copia a curva do modelo (sem segunda fonte de
  // verdade no fio, que poderia defasar se o modelo for editado depois da seleção).
  const setMotorModel = (group, modelId) => {
    setMotorEdits((prev) => ({
      ...prev,
      [group.motor_id]: {
        ...prev[group.motor_id],
        role: prev[group.motor_id]?.role ?? group.role,
        engine_model_id: modelId === NO_MODEL ? '' : modelId,
      },
    }));
  };

  const setRegimes = (motorId, role, regimes) => setMotorEdits(prev => ({
    ...prev,
    [motorId]: { ...prev[motorId], role: prev[motorId]?.role ?? role, regimes },
  }));

  // Curadoria por warning (label/severity/enabled). Semeia do catálogo EFETIVO
  // (override em progresso OU seed do group) para NUNCA perder overrides de outros
  // warnings — o backend substitui o warnings_catalog inteiro (sanitize sobrescreve),
  // então enviamos o efetivo com o delta aplicado. Merge imutável, padrão setRegimes.
  const setWarning = (group, warning, patch) => setMotorEdits(prev => {
    const base = prev[group.motor_id]?.warnings_catalog ?? group.warnings_catalog ?? {};
    return {
      ...prev,
      [group.motor_id]: {
        ...prev[group.motor_id],
        role: prev[group.motor_id]?.role ?? group.role,
        warnings_catalog: { ...base, [warning]: { ...(base[warning] || {}), ...patch } },
      },
    };
  });

  // Edição dos 2 params de gearbox por-motor (D-04/D-06/D-07): merge imutável
  // a partir do EFETIVO (mesmo padrão de setWarning) — omitir/limpar um campo
  // (string vazia) REMOVE a chave (nunca grava 0/""), pois sanitize_gearbox_params
  // no backend descarta valores <=0/não-coercíveis (motor sem ratio volta a
  // sombra). Ambos os campos são numéricos.
  const setGearboxField = (group, field, value) => setMotorEdits(prev => {
    const base = prev[group.motor_id]?.gearbox ?? group.gearbox ?? {};
    const next = { ...base };
    if (value === '' || value === null || value === undefined) {
      delete next[field];
    } else {
      next[field] = Number(value);
    }
    return {
      ...prev,
      [group.motor_id]: {
        ...prev[group.motor_id],
        role: prev[group.motor_id]?.role ?? group.role,
        gearbox: next,
      },
    };
  });

  // Edição de 1 regra de alerta por-motor (D-21/D-22): merge imutável a partir
  // do EFETIVO (mesmo padrão de setWarning/setGearboxField) — o backend
  // sanitize_motor_rules faz whole-replace do alert_rules do motor, então
  // semeamos do efetivo para nunca perder outras regras não editadas.
  const setRule = (group, ruleId, patch) => setMotorEdits(prev => {
    const base = prev[group.motor_id]?.alert_rules ?? group.alert_rules ?? {};
    return {
      ...prev,
      [group.motor_id]: {
        ...prev[group.motor_id],
        role: prev[group.motor_id]?.role ?? group.role,
        alert_rules: { ...base, [ruleId]: { ...(base[ruleId] || {}), ...patch } },
      },
    };
  });

  // Troca de tipo (Analógica<->Digital): reseta os campos específicos do tipo
  // anterior (op/threshold vs active_when) e limpa o path (as opções do
  // dropdown mudam de fonte — group.sources vs allGroups "Outros").
  const setRuleType = (group, ruleId, newType) => {
    if (newType === 'digital') {
      setRule(group, ruleId, {
        type: 'digital', path: '', active_when: 1, op: undefined, threshold: undefined,
      });
    } else {
      setRule(group, ruleId, {
        type: 'analog', path: '', op: 'lt', threshold: null, active_when: undefined,
      });
    }
  };

  const addRule = (group) => {
    const ruleId = `rule_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
    setRule(group, ruleId, {
      type: 'analog', path: '', op: 'lt', threshold: null, severity: 'medium', label: '', enabled: true,
    });
  };

  const removeRule = (group, ruleId) => setMotorEdits(prev => {
    const base = prev[group.motor_id]?.alert_rules ?? group.alert_rules ?? {};
    const next = { ...base };
    delete next[ruleId];
    return {
      ...prev,
      [group.motor_id]: {
        ...prev[group.motor_id],
        role: prev[group.motor_id]?.role ?? group.role,
        alert_rules: next,
      },
    };
  });

  // Toggle da auditoria (D-05): expande/colapsa e busca GET /api/lab/gearbox-events
  // só na 1ª expansão do motor nesta sessão (sem componente shadcn novo — useState
  // local, mesmo padrão de "Ver últimos eventos" descrito no UI-SPEC §2).
  const toggleGearboxEvents = async (group) => {
    const motorId = group.motor_id;
    const isOpen = !!expandedGearboxEvents[motorId];
    setExpandedGearboxEvents((prev) => ({ ...prev, [motorId]: !isOpen }));
    if (isOpen || gearboxEvents[motorId]) return; // já colapsando ou já carregado
    try {
      setLoadingGearboxEvents((prev) => ({ ...prev, [motorId]: true }));
      const res = await axios.get(`${API}/lab/gearbox-events/${equipmentId}`, {
        params: { motor_id: motorId, limit: 20 },
      });
      setGearboxEvents((prev) => ({ ...prev, [motorId]: res.data?.events || [] }));
    } catch (error) {
      console.error('Erro ao carregar eventos de reversão da gearbox:', error);
      setGearboxEvents((prev) => ({ ...prev, [motorId]: [] })); // não quebra a UI
    } finally {
      setLoadingGearboxEvents((prev) => ({ ...prev, [motorId]: false }));
    }
  };

  // Rótulos PT-BR do resultado classificado (gearbox_detection.py) — NUNCA
  // renderiza a string crua "abuso"/"violação" (UI-SPEC §2). ok→verde;
  // warning/critical→cor de severityConfig.high/critical (D-01, zero drift);
  // shadow_uncalibrated/indeterminate→neutro (motor ainda em sombra ou gap de dado).
  const GEARBOX_RESULT_LABELS = {
    ok: 'OK',
    warning: 'Atenção',
    critical: 'Crítico',
    shadow_uncalibrated: 'Sem calibração',
    indeterminate: 'Indeterminado',
  };

  const renderGearboxResultBadge = (result) => {
    if (result === 'ok') {
      return (
        <Badge variant="outline" className="bg-green-500/15 text-green-500 border-green-500/30">
          {GEARBOX_RESULT_LABELS.ok}
        </Badge>
      );
    }
    if (result === 'warning' || result === 'critical') {
      const cfg = severityConfig[result === 'critical' ? 'critical' : 'high'];
      return (
        <Badge variant="outline" className={cn(cfg.bg, cfg.color, cfg.border)}>
          {GEARBOX_RESULT_LABELS[result]}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-muted text-muted-foreground border-border">
        {GEARBOX_RESULT_LABELS[result] || result}
      </Badge>
    );
  };

  const dirty = Object.keys(motorEdits).length > 0 || removeMotors.length > 0;

  const handleSave = async () => {
    try {
      setSaving(true);
      const motors = Object.entries(motorEdits)
        .filter(([id]) => !removeMotors.includes(id)) // remoção vence sobre edição
        .map(([id, edit]) => ({ id, ...edit })); // inclui engine_model_id (a curva é resolvida no backend)
      // PARCIAL: sem `signals` no payload — LiveConfigUpdate.signals default [] e o
      // handler só toca sinais `if update.signals:` (nunca apaga o cadastro de sinais).
      await axios.put(`${API}/equipments/${equipmentId}/live-config`, {
        motors,
        remove_motors: removeMotors,
      });
      toast.success('Cadastro de motores salvo');
      await fetchConfig();
    } catch (error) {
      console.error('Erro ao salvar cadastro de motores:', error);
      // Copy da subseção "Regras de alerta" (Fase 17.1-07) — fallback ao
      // detail específico do backend, mesma voz de EquipmentAlertConfig.js:306.
      toast.error(error.response?.data?.detail || 'Erro ao salvar regras do motor');
    } finally {
      setSaving(false);
    }
  };

  // Identidade do motor: modelo do catálogo (curva herdada) — junto de
  // papel/posição, antes de regimes (comportamento).
  const renderLoadCurve = (group) => {
    const modelId = effectiveModelId(group);
    const selectedModel = engineModels.find((m) => m.id === modelId);
    const points = selectedModel?.load_curve?.length || 0; // curva vem do catálogo (fonte única)
    const modelLabel = (m) => [m.brand, m.model, m.rating && `(${m.rating})`].filter(Boolean).join(' ');
    const emptyCatalog = engineModels.length === 0;

    return (
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-border/50">
        <Label className="text-xs text-muted-foreground flex items-center gap-1">
          <Gauge className="h-3.5 w-3.5" /> Modelo do motor (curva de carga)
        </Label>
        <div className="flex-1" />
        {selectedModel && points >= 2 && (
          <span className="text-[11px] text-muted-foreground">{points} pontos herdados</span>
        )}
        <Select value={modelId || NO_MODEL} onValueChange={(v) => setMotorModel(group, v)}>
          <SelectTrigger className="w-[220px] h-7 text-xs" data-testid={`motor-model-${group.motor_id}`}>
            <SelectValue placeholder={emptyCatalog ? "Cadastre em 'Modelos de Motor'" : 'Modelo do catálogo'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_MODEL}>Nenhum (sem curva)</SelectItem>
            {engineModels.map((m) => (
              <SelectItem key={m.id} value={m.id}>{modelLabel(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  // RPM ilustrativo do label de interpretação da fórmula (pedido do dono na
  // UAT: traduzir os params preenchidos em significado, em vez de nota livre).
  const EXAMPLE_DISENGAGE_RPM = 1500;

  // Reversão da gearbox (Fase 17, D-04/D-05/D-07/D-08/D-09): params calibráveis
  // por-motor para o alerta gearbox_abuse — mesmo molde de renderLoadCurve/"Avisos
  // do motor". Badge de status ("em sombra"/"calibrado") é o ponto focal (UI-SPEC
  // §1): comunica de relance se o motor já saiu do modo sombra (D-04). Sem
  // client-side range-check (fronteira de validação é sanitize_gearbox_params
  // no backend, mesmo princípio dos outros campos numéricos deste arquivo).
  const renderGearboxSection = (group) => {
    const gearbox = effectiveGearbox(group);
    const calibrated = Number(gearbox.ratio_rpm_s) > 0;
    const motorId = group.motor_id;
    const isExpanded = !!expandedGearboxEvents[motorId];
    const isLoadingEvents = !!loadingGearboxEvents[motorId];
    const events = gearboxEvents[motorId];
    const eventsLabel = events ? `Ver últimos eventos (${events.length})` : 'Ver últimos eventos';

    return (
      <div className="px-3 py-2 border-t border-border/50 space-y-1.5">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground flex items-center gap-1 flex-1">
            <ArrowLeftRight className="h-3.5 w-3.5" /> Reversão da gearbox (caixa
            reversora) — parâmetros para o alerta de reversão brusca
          </Label>
          {calibrated ? (
            <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-green-500/15 text-green-500 border border-green-500/30 shrink-0">
              calibrado
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-yellow-500/15 text-yellow-500 border border-yellow-500/30 shrink-0">
              em sombra
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground">Ratio (RPM/s)</span>
          <Input
            className="h-7 w-28 text-xs"
            type="number"
            placeholder="ex.: 40"
            value={gearbox.ratio_rpm_s ?? ''}
            onChange={(e) => setGearboxField(group, 'ratio_rpm_s', e.target.value)}
            data-testid={`gearbox-ratio-${group.motor_id}`}
          />
          <span className="text-[10px] text-muted-foreground">RPM engate seguro</span>
          <Input
            className="h-7 w-28 text-xs"
            type="number"
            placeholder="ex.: 600"
            value={gearbox.safe_engage_rpm ?? ''}
            onChange={(e) => setGearboxField(group, 'safe_engage_rpm', e.target.value)}
            data-testid={`gearbox-safe-engage-${group.motor_id}`}
          />
        </div>
        {calibrated && Number(gearbox.safe_engage_rpm) > 0 ? (
          <p
            className="text-xs text-muted-foreground"
            data-testid={`gearbox-interpretation-${group.motor_id}`}
          >
            Com estes valores: desengatando a {EXAMPLE_DISENGAGE_RPM.toLocaleString('pt-BR')} RPM,
            o motor precisa de ~{Math.max(0,
              (EXAMPLE_DISENGAGE_RPM - Number(gearbox.safe_engage_rpm)) / Number(gearbox.ratio_rpm_s)
            ).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s em Neutral
            (caindo {Number(gearbox.ratio_rpm_s).toLocaleString('pt-BR')} RPM/s até
            os {Number(gearbox.safe_engage_rpm).toLocaleString('pt-BR')} RPM de engate
            seguro) para a reversão ser segura — menos tempo que isso gera o alerta.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Sem ratio calibrado, o motor permanece em modo sombra: os eventos de
            reversão são registrados para auditoria, mas o alerta não soa para o
            operador.
          </p>
        )}

        {/* Auditoria dos eventos-sombra (D-05): lista somente-leitura embutida
            na mesma subseção — o dono ajusta ratio_rpm_s olhando os últimos
            eventos sem trocar de tela. */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => toggleGearboxEvents(group)}
          data-testid={`gearbox-events-toggle-${motorId}`}
        >
          {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {eventsLabel}
        </Button>
        {isExpanded && (
          <div className="mt-1.5">
            {isLoadingEvents ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Carregando eventos...
              </div>
            ) : events && events.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="h-7 text-[11px]">Data/hora</TableHead>
                    <TableHead className="h-7 text-[11px]">RPM no desengate</TableHead>
                    <TableHead className="h-7 text-[11px]">Tempo em Neutral (s)</TableHead>
                    <TableHead className="h-7 text-[11px]">Mínimo estimado (s)</TableHead>
                    <TableHead className="h-7 text-[11px]">Resultado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((ev) => (
                    <TableRow key={ev.id || `${motorId}-${ev.detected_at}`}>
                      <TableCell className="text-xs">
                        {ev.detected_at ? new Date(ev.detected_at).toLocaleString('pt-BR') : '—'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {ev.rpm_at_disengage != null ? ev.rpm_at_disengage.toFixed(0) : '—'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {ev.neutral_duration_s != null ? ev.neutral_duration_s.toFixed(1) : '—'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {ev.min_estimated_s != null ? ev.min_estimated_s.toFixed(1) : '—'}
                      </TableCell>
                      <TableCell className="text-xs">{renderGearboxResultBadge(ev.result)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-xs text-muted-foreground py-1">
                Nenhum evento de reversão registrado ainda para este motor.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Regras de alerta por motor (Fase 17.1-07, D-21/D-22/D-23): analógica
  // (path relativo ao motor + operador + threshold) ou digital (path COMPLETO
  // qualificado + ATIVO/INATIVO), com label PT-BR + severidade + switch Ativo
  // — mesmo molde de "Avisos do motor"/gearbox acima. Dropdown de path 100%
  // data-driven (analogPathOptions/digitalPathOptions), zero lista curada.
  const renderAlertRulesSection = (group) => {
    const rules = effectiveRules(group);
    const ruleIds = Object.keys(rules);
    const analogOptions = analogPathOptions(group);
    const digitalOptions = digitalPathOptions(group.motor_id);

    return (
      <div className="px-3 py-2 border-t border-border/50 space-y-1.5">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground flex items-center gap-1 flex-1">
            <Siren className="h-3.5 w-3.5" /> Regras de alerta (threshold
            analógico ou campo digital ATIVO/INATIVO — dispara alerta na
            Central de Alertas)
          </Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => addRule(group)}
            data-testid={`alert-rule-add-${group.motor_id}`}
          >
            <Plus className="h-3 w-3" /> Adicionar regra
          </Button>
        </div>
        {ruleIds.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Nenhuma regra — sem parâmetro, zero alerta.
          </p>
        )}
        {ruleIds.map((ruleId) => {
          const rule = rules[ruleId];
          const isDigital = rule.type === 'digital';
          const options = withCurrentPath(
            isDigital ? digitalOptions : analogOptions,
            rule.path
          );
          const noOptions = options.length === 0;
          return (
            <div
              key={ruleId}
              className="flex flex-wrap items-center gap-1.5 rounded border border-border/50 p-1.5"
            >
              <Select
                value={rule.type || 'analog'}
                onValueChange={(value) => setRuleType(group, ruleId, value)}
              >
                <SelectTrigger className="h-7 w-[150px] text-xs" data-testid={`alert-rule-type-${group.motor_id}-${ruleId}`}>
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  {RULE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={rule.path || undefined}
                onValueChange={(value) => setRule(group, ruleId, { path: value })}
                disabled={noOptions}
              >
                <SelectTrigger className="h-7 w-[240px] text-xs" data-testid={`alert-rule-path-${group.motor_id}-${ruleId}`}>
                  <SelectValue placeholder={noOptions ? 'Sem path publicado ainda' : 'Path (do que o motor publica)'} />
                </SelectTrigger>
                <SelectContent>
                  {options.map((opt) => (
                    <SelectItem key={opt.path} value={opt.path}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isDigital ? (
                <Select
                  value={String(rule.active_when ?? 1)}
                  onValueChange={(value) => setRule(group, ruleId, { active_when: Number(value) })}
                >
                  <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`alert-rule-activewhen-${group.motor_id}-${ruleId}`}>
                    <SelectValue placeholder="Quando" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVE_WHEN_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <>
                  <Select
                    value={rule.op || 'lt'}
                    onValueChange={(value) => setRule(group, ruleId, { op: value })}
                  >
                    <SelectTrigger className="h-7 w-[70px] text-xs" data-testid={`alert-rule-op-${group.motor_id}-${ruleId}`}>
                      <SelectValue placeholder="Op" />
                    </SelectTrigger>
                    <SelectContent>
                      {RULE_OP_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    className="h-7 w-24 text-xs"
                    type="number"
                    placeholder="threshold"
                    value={rule.threshold ?? ''}
                    onChange={(e) => setRule(group, ruleId, {
                      threshold: e.target.value === '' ? null : Number(e.target.value),
                    })}
                    data-testid={`alert-rule-threshold-${group.motor_id}-${ruleId}`}
                  />
                </>
              )}
              <Input
                className="h-7 w-[200px] text-xs"
                placeholder="Rótulo (ex.: Pressão água salgada baixa)"
                value={rule.label || ''}
                onChange={(e) => setRule(group, ruleId, { label: e.target.value })}
                data-testid={`alert-rule-label-${group.motor_id}-${ruleId}`}
              />
              <Select
                value={rule.severity || 'medium'}
                onValueChange={(value) => setRule(group, ruleId, { severity: value })}
              >
                <SelectTrigger className="h-7 w-[110px] text-xs" data-testid={`alert-rule-severity-${group.motor_id}-${ruleId}`}>
                  <SelectValue placeholder="Severidade" />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1.5">
                <Switch
                  checked={rule.enabled !== false}
                  onCheckedChange={(checked) => setRule(group, ruleId, { enabled: checked })}
                  data-testid={`alert-rule-enabled-${group.motor_id}-${ruleId}`}
                />
                <span className="text-[10px] text-muted-foreground w-10">
                  {rule.enabled !== false ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                title="Remover regra"
                aria-label="Remover regra de alerta"
                onClick={() => removeRule(group, ruleId)}
                data-testid={`alert-rule-remove-${group.motor_id}-${ruleId}`}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMotorGroup = (group) => {
    const role = effectiveRole(group);
    // Cadastro do motor: registrado no backend OU com cadastro iniciado nesta
    // sessão ("Cadastrar motor"); remoção pendente vence sobre tudo.
    const pendingRemoval = removeMotors.includes(group.motor_id);
    const isRegistered = group.registered !== false || !!motorEdits[group.motor_id];

    return (
      <div key={group.key} className="rounded-lg border border-border overflow-hidden">
        {/* Header do grupo: nome + status de cadastro */}
        <div className="flex items-center gap-2 p-3 bg-muted/30">
          <span className="text-sm font-medium truncate flex-1" data-testid={`motor-group-label-${group.key}`}>
            {group.label}
          </span>
          {pendingRemoval && (
            <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-red-500/15 text-red-400 border border-red-500/30 shrink-0">
              remoção pendente
            </span>
          )}
          {!pendingRemoval && !isRegistered && (
            <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-yellow-500/15 text-yellow-500 border border-yellow-500/30 shrink-0"
                  title="Instância detectada no barramento mas fora do cadastro — não aparece na página Ao vivo. Use 'Cadastrar motor' para promovê-la.">
              não cadastrado
            </span>
          )}
        </div>

        {/* Controles de cadastro do motor */}
        {pendingRemoval && (
          <div className="flex items-center gap-2 px-3 py-2 border-t border-border/50">
            <p className="text-xs text-muted-foreground flex-1">
              Será removido do cadastro ao salvar — a instância volta a "não
              cadastrada" e sai da página Ao vivo (a telemetria não é apagada).
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1"
              onClick={() => setRemoveMotors(prev => prev.filter(id => id !== group.motor_id))}
              data-testid={`motor-undo-remove-${group.motor_id}`}
            >
              <Undo2 className="h-3.5 w-3.5" /> Desfazer
            </Button>
          </div>
        )}
        {!pendingRemoval && !isRegistered && (
          <div className="flex items-center gap-2 px-3 py-2 border-t border-border/50">
            <p className="text-xs text-muted-foreground flex-1">
              Espelho de fábrica ou instância nova — cadastre apenas se for um
              motor REAL deste equipamento.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => setMotorEdits(prev => ({
                ...prev,
                [group.motor_id]: {
                  role: group.role || 'propulsion',
                  ...(group.position ? { position: group.position } : {}),
                },
              }))}
              data-testid={`motor-register-${group.motor_id}`}
            >
              <Plus className="h-3.5 w-3.5" /> Cadastrar motor
            </Button>
          </div>
        )}
        {!pendingRemoval && isRegistered && (
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-border/50">
            <Select
              value={role}
              onValueChange={(value) => setMotorEdits(prev => ({
                ...prev,
                [group.motor_id]: {
                  ...prev[group.motor_id],
                  role: value,
                  // posição só existe p/ propulsão
                  ...(value !== 'propulsion' ? { position: undefined } : {}),
                },
              }))}
            >
              <SelectTrigger className="w-[160px] h-7 text-xs" data-testid={`motor-role-${group.motor_id}`}>
                <SelectValue placeholder="Papel" />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {role === 'propulsion' && (
              <Select
                value={effectivePosition(group) || ''}
                onValueChange={(value) => setMotorEdits(prev => ({
                  ...prev,
                  [group.motor_id]: { ...prev[group.motor_id], role, position: value },
                }))}
              >
                <SelectTrigger className="w-[150px] h-7 text-xs" data-testid={`motor-position-${group.motor_id}`}>
                  <SelectValue placeholder="Posição" />
                </SelectTrigger>
                <SelectContent>
                  {POSITION_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1 text-muted-foreground hover:text-destructive"
              onClick={() => setRemoveMotors(prev => [...prev, group.motor_id])}
              title="Descadastrar: a instância sai da página Ao vivo e volta a 'não cadastrada' (telemetria preservada)"
              data-testid={`motor-remove-${group.motor_id}`}
            >
              <Trash2 className="h-3.5 w-3.5" /> Remover do cadastro
            </Button>
          </div>
        )}

        {!pendingRemoval && isRegistered && renderLoadCurve(group)}

        {/* Regimes de operação do motor (faixas RPM × L/h × engate — primeira
            regra que casa vence; set-points por motor, nada hard-coded) */}
        {!pendingRemoval && isRegistered && (
          <div className="px-3 py-2 border-t border-border/50 space-y-1.5">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground flex-1">
                Regimes de operação (identificam o que o motor está fazendo:
                RPM × consumo × engate da caixa)
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setRegimes(group.motor_id, role,
                  (SUGGESTED_REGIMES[role] || SUGGESTED_REGIMES.motor).map(r => ({ ...r })))}
                title="Preenche com as faixas sugeridas para o papel do motor (calibráveis)"
                data-testid={`regimes-suggest-${group.motor_id}`}
              >
                Sugerir pelo papel
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => setRegimes(group.motor_id, role, [
                  ...effectiveRegimes(group),
                  { id: '', label: '', rpm_min: null, rpm_max: null, fuel_lph_min: null, fuel_lph_max: null, gear: null },
                ])}
                data-testid={`regimes-add-${group.motor_id}`}
              >
                <Plus className="h-3 w-3" /> Regra
              </Button>
            </div>
            {effectiveRegimes(group).length === 0 && (
              <p className="text-xs text-muted-foreground">
                Nenhum regime cadastrado — o Ao vivo não exibe o regime deste motor.
              </p>
            )}
            {effectiveRegimes(group).map((rule, idx) => {
              const rules = effectiveRegimes(group);
              const patch = (changes) => {
                const next = rules.map((r, i) => (i === idx ? { ...r, ...changes } : r));
                setRegimes(group.motor_id, role, next);
              };
              const numVal = (v) => (v === null || v === undefined ? '' : v);
              return (
                <div key={idx} className="flex flex-wrap items-center gap-1.5">
                  <Input
                    className="h-7 w-[150px] text-xs"
                    placeholder="Nome (ex.: Bomba de sucção)"
                    value={rule.label || ''}
                    onChange={(e) => patch({ label: e.target.value })}
                    data-testid={`regime-label-${group.motor_id}-${idx}`}
                  />
                  <span className="text-[10px] text-muted-foreground">RPM</span>
                  <Input className="h-7 w-16 text-xs" type="number" placeholder="mín"
                    value={numVal(rule.rpm_min)}
                    onChange={(e) => patch({ rpm_min: e.target.value === '' ? null : Number(e.target.value) })} />
                  <Input className="h-7 w-16 text-xs" type="number" placeholder="máx"
                    value={numVal(rule.rpm_max)}
                    onChange={(e) => patch({ rpm_max: e.target.value === '' ? null : Number(e.target.value) })} />
                  <span className="text-[10px] text-muted-foreground">L/h</span>
                  <Input className="h-7 w-16 text-xs" type="number" placeholder="mín"
                    value={numVal(rule.fuel_lph_min)}
                    onChange={(e) => patch({ fuel_lph_min: e.target.value === '' ? null : Number(e.target.value) })} />
                  <Input className="h-7 w-16 text-xs" type="number" placeholder="máx"
                    value={numVal(rule.fuel_lph_max)}
                    onChange={(e) => patch({ fuel_lph_max: e.target.value === '' ? null : Number(e.target.value) })} />
                  <Select
                    value={rule.gear || 'any'}
                    onValueChange={(value) => patch({ gear: value === 'any' ? null : value })}
                  >
                    <SelectTrigger className="h-7 w-[110px] text-xs">
                      <SelectValue placeholder="Engate" />
                    </SelectTrigger>
                    <SelectContent>
                      {GEAR_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    title="Remover regra" aria-label="Remover regra de regime"
                    onClick={() => setRegimes(group.motor_id, role, rules.filter((_, i) => i !== idx))}
                    data-testid={`regime-remove-${group.motor_id}-${idx}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Avisos do motor (D-07/D-09): curadoria por warning do catálogo EFETIVO
            (seed global + override por-motor do Plano 03). Uma linha por warning:
            rótulo PT-BR editável + severidade + liga/desliga. NUNCA renderiza a
            chave crua do warning como label — sempre cfg.label. */}
        {!pendingRemoval && isRegistered && (
          <div className="px-3 py-2 border-t border-border/50 space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              Avisos do motor (severidade e rótulo de cada alarme do painel —
              usados na Central de Alertas e no Ao vivo)
            </Label>
            {Object.keys(effectiveWarnings(group)).length === 0 && (
              <p className="text-xs text-muted-foreground">
                Catálogo de avisos vazio para este motor.
              </p>
            )}
            {Object.entries(effectiveWarnings(group))
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([warning, cfg]) => (
                <div key={warning} className="flex flex-wrap items-center gap-1.5">
                  <Input
                    className="h-7 w-[220px] text-xs"
                    placeholder="Rótulo do aviso"
                    value={cfg.label || ''}
                    onChange={(e) => setWarning(group, warning, { label: e.target.value })}
                    data-testid={`warning-label-${group.motor_id}-${warning}`}
                  />
                  <Select
                    value={cfg.severity || 'medium'}
                    onValueChange={(value) => setWarning(group, warning, { severity: value })}
                  >
                    <SelectTrigger className="h-7 w-[120px] text-xs" data-testid={`warning-severity-${group.motor_id}-${warning}`}>
                      <SelectValue placeholder="Severidade" />
                    </SelectTrigger>
                    <SelectContent>
                      {SEVERITY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1.5">
                    <Switch
                      checked={cfg.enabled !== false}
                      onCheckedChange={(checked) => setWarning(group, warning, { enabled: checked })}
                      data-testid={`warning-enabled-${group.motor_id}-${warning}`}
                    />
                    <span className="text-[10px] text-muted-foreground w-10">
                      {cfg.enabled !== false ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        )}

        {!pendingRemoval && isRegistered && renderAlertRulesSection(group)}

        {!pendingRemoval && isRegistered && renderGearboxSection(group)}
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="motors-config">
      {/* Catálogo de modelos de motor — área nomeada (não campo solto). Aqui a
          curva é criada/editada; cada motor abaixo só a HERDA via dropdown
          (D-05: link acessível na aba Motores). */}
      <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/20 p-2.5">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Modelos de Motor</span> — marca/modelo/rating
          e a curva de plena carga (para o cálculo de carga). Os motores herdam a curva do modelo.
          Catálogo <span className="italic">compartilhado entre equipamentos</span>: editar um modelo
          propaga a curva a todos os motores associados.
        </p>
        <EngineModelsManager onChanged={fetchEngineModels} />
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando motores...
        </div>
      )}

      {!loading && groups !== null && groups.length === 0 && (
        <p className="text-sm text-muted-foreground py-2">
          Nenhum motor detectado ainda — o cadastro fica disponível quando o
          equipamento enviar telemetria.
        </p>
      )}

      {!loading && (groups || []).map(renderMotorGroup)}

      {!loading && groups !== null && groups.length > 0 && (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={!dirty || saving}
            data-testid="motors-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Salvar motores
          </Button>
        </div>
      )}
    </div>
  );
};

export default MotorsConfig;
